import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Search, X, Loader2, MapPin } from "lucide-react";
import { useLang } from "@/lib/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SAMPLES = [
  "439 NW 59 Ter, Miami, FL",
  "291 NW 52 St, Miami, FL",
  "111 NW 1st St, Miami, FL",
  "3500 Pan American Dr, Miami, FL",
];

export const AddressSearchBar = ({ onSearch, loading }) => {
  const { t } = useLang();
  const [value, setValue] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(-1);
  const boxRef = useRef(null);
  const debounce = useRef(null);

  const submit = (addr) => {
    const q = (addr ?? value).trim();
    if (!q) return;
    setValue(q);
    setOpen(false);
    onSearch(q);
  };

  // Debounced official-address autocomplete against Miami-Dade parcels.
  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    if (value.trim().length < 3) {
      setSuggestions([]);
      return;
    }
    debounce.current = setTimeout(async () => {
      const controller = new AbortController();
      try {
        const { data } = await axios.get(`${API}/property/suggest`, { params: { q: value }, signal: controller.signal });
        setSuggestions(data || []);
        setOpen((data || []).length > 0);
        setActive(-1);
      } catch {
        /* aborted or failed */
      }
    }, 250);
    return () => debounce.current && clearTimeout(debounce.current);
  }, [value]);

  useEffect(() => {
    const onClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const onKeyDown = (e) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((a) => Math.min(a + 1, suggestions.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((a) => Math.max(a - 1, 0));
    } else if (e.key === "Enter" && active >= 0) {
      e.preventDefault();
      const s = suggestions[active];
      submit(`${s.address}${s.city ? ", " + s.city : ""}`);
    } else if (e.key === "Escape") {
      setOpen(false);
    }
  };

  return (
    <div className="relative w-full" ref={boxRef}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          submit();
        }}
        className="flex flex-col gap-3 sm:flex-row"
      >
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
          <Input
            data-testid="address-search-input"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={onKeyDown}
            onFocus={() => suggestions.length > 0 && setOpen(true)}
            autoComplete="off"
            placeholder={t("search.placeholder")}
            className="h-14 rounded-xl border-border bg-card pl-12 pr-11 text-base shadow-sm focus-visible:ring-primary"
          />
          {value && (
            <button
              type="button"
              data-testid="address-search-clear-btn"
              onClick={() => {
                setValue("");
                setSuggestions([]);
              }}
              className="absolute right-3 top-1/2 -translate-y-1/2 rounded-full p-1 text-muted-foreground hover:bg-secondary hover:text-foreground"
            >
              <X className="h-4 w-4" />
            </button>
          )}

          {open && suggestions.length > 0 && (
            <ul
              data-testid="address-suggestions"
              className="absolute left-0 right-0 top-full z-50 mt-2 max-h-72 overflow-auto rounded-xl border border-border bg-card py-1 shadow-lg"
            >
              {suggestions.map((s, i) => (
                <li key={`${s.folio}-${i}`}>
                  <button
                    type="button"
                    data-testid="address-suggestion-item"
                    onMouseEnter={() => setActive(i)}
                    onClick={() => submit(`${s.address}${s.city ? ", " + s.city : ""}`)}
                    className={`flex w-full items-center gap-2.5 px-4 py-2.5 text-left text-sm ${
                      active === i ? "bg-secondary" : "hover:bg-secondary/60"
                    }`}
                  >
                    <MapPin className="h-3.5 w-3.5 shrink-0 text-accent" />
                    <span className="font-medium text-foreground">{s.address}</span>
                    <span className="ml-auto font-mono text-[10px] uppercase tracking-wider text-muted-foreground">
                      {s.city}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
        <Button
          type="submit"
          data-testid="address-search-submit-btn"
          disabled={loading}
          className="h-14 gap-2 rounded-xl px-7 text-base font-semibold"
        >
          {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Search className="h-5 w-5" />}
          {loading ? t("search.loading") : t("search.button")}
        </Button>
      </form>

      <div className="mt-4 flex flex-wrap items-center gap-2">
        <span className="mr-1 font-mono text-[10px] uppercase tracking-[0.18em] text-muted-foreground">
          {t("search.try")}
        </span>
        {SAMPLES.map((s) => (
          <button
            key={s}
            type="button"
            data-testid="sample-address-button"
            onClick={() => submit(s)}
            className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1.5 text-xs font-medium text-foreground/80 transition-colors hover:border-primary/50 hover:text-primary"
          >
            <MapPin className="h-3 w-3 text-accent" />
            {s.split(",")[0]}
          </button>
        ))}
      </div>
    </div>
  );
};
